import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, ShoppingCart, Truck, ShieldCheck, RotateCcw, Minus, Plus, ChevronRight, Boxes } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { ComingSoonBadge, ComingSoonOverlay } from "@/components/ComingSoonBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const INV = { in_stock: ["In Stock", "text-[#00E676]"], low_stock: ["Low Stock — order soon", "text-[#FFB800]"], out_of_stock: ["Sold Out", "text-[#FF2A6D]"] };

export default function ProductDetail() {
  const { id } = useParams();
  const { format } = useCurrency();
  const { addItem } = useCart();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [id]);

  if (data === null) return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-[#00F0FF] border-t-transparent" /></div>;
  if (data === false) return <div className="mx-auto max-w-7xl px-4 py-20 text-center text-slate-400">Product not found. <Link to="/shop" className="text-[#00F0FF]">Back to shop</Link></div>;

  const p = data.product;
  const inv = INV[p.inventory_status] || INV.in_stock;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <nav className="mb-6 flex items-center gap-1.5 text-xs text-slate-500">
        <Link to="/" className="hover:text-[#00F0FF]">Home</Link><ChevronRight className="h-3 w-3" />
        <Link to={`/shop?category=${p.category}`} className="capitalize hover:text-[#00F0FF]">{p.category.replace("-", " ")}</Link><ChevronRight className="h-3 w-3" />
        <span className="text-slate-300">{p.name}</span>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="relative aspect-square overflow-hidden rounded-3xl border border-white/10 bg-[#0B0D16]">
          <img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover opacity-90" data-testid="product-cover-image" />
          {p.coming_soon && <ComingSoonOverlay />}
        </motion.div>

        <div>
          <div className="flex items-center gap-3">
            <span className={`font-mono text-xs font-bold uppercase tracking-wider ${inv[1]}`}>{inv[0]}</span>
            <span className="flex items-center gap-1 text-sm text-slate-300"><Star className="h-4 w-4 fill-[#FFB800] text-[#FFB800]" /> {p.rating} <span className="text-slate-500">({p.reviews})</span></span>
          </div>
          <h1 className="font-display mt-3 text-3xl font-bold text-white sm:text-4xl" data-testid="product-title">{p.name}</h1>

          <div className="mt-5 flex items-end gap-3">
            <span className="font-mono text-3xl font-extrabold text-white" data-testid="product-price">{format(p.price_usd)}</span>
            {p.compare_at_usd && <span className="font-mono text-lg text-slate-500 line-through">{format(p.compare_at_usd)}</span>}
          </div>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
            {p.coming_soon && <div className="mb-3 flex items-center gap-2"><ComingSoonBadge /></div>}
            <p className="text-sm leading-relaxed text-slate-300">{p.description}</p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <div className="flex items-center rounded-xl border border-white/10 bg-white/5">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:text-[#00F0FF]" data-testid="qty-dec"><Minus className="h-4 w-4" /></button>
              <span className="w-10 text-center font-mono" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:text-[#00F0FF]" data-testid="qty-inc"><Plus className="h-4 w-4" /></button>
            </div>
            <Button onClick={() => addItem(p, qty)} className="flex-1 rounded-xl bg-[#00F0FF] py-6 text-base font-bold text-[#08090E] hover:bg-[#33F3FF] glow-cyan" data-testid="pdp-add-to-cart">
              <ShoppingCart className="mr-2 h-5 w-5" /> Add to Cart
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {[[Truck, "Free ship $75+"], [ShieldCheck, "Buyer protection"], [RotateCcw, "30-day returns"]].map(([I, t], i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 rounded-xl border border-white/10 p-3 text-center"><I className="h-5 w-5 text-[#00F0FF]" /><span className="text-[11px] text-slate-400">{t}</span></div>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-slate-400">
            <Boxes className="h-4 w-4 text-[#8A2BE2]" /> Fulfilled via <span className="font-semibold text-slate-200">{p.supplier?.preferred || "TeamDrop"}</span> global network · backup {p.supplier?.backup || "AliExpress"}
          </div>
        </div>
      </div>

      <div className="mt-14">
        <Tabs defaultValue="specs">
          <TabsList className="bg-white/5">
            <TabsTrigger value="specs" data-testid="tab-specs">Specifications</TabsTrigger>
            <TabsTrigger value="shipping" data-testid="tab-shipping-info">Shipping</TabsTrigger>
          </TabsList>
          <TabsContent value="specs" className="mt-5">
            <div className="dw-card overflow-hidden rounded-2xl">
              {p.specs?.map((s, i) => (
                <div key={i} className={`flex justify-between px-6 py-4 ${i % 2 ? "bg-white/[0.02]" : ""}`}>
                  <span className="text-sm text-slate-400">{s.label}</span><span className="font-mono text-sm text-slate-200">{s.value}</span>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="shipping" className="mt-5">
            <div className="dw-card rounded-2xl p-6 text-sm text-slate-300">
              Ships worldwide from our global fulfilment network. Free standard shipping on orders over $75. Estimated delivery 7–15 business days depending on destination. Full tracking provided.
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {data.related?.length > 0 && (
        <div className="mt-16">
          <h2 className="font-display mb-6 text-2xl font-bold text-white">You might also like</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{data.related.map((r, i) => <ProductCard key={r.product_id} product={r} index={i} />)}</div>
        </div>
      )}
    </div>
  );
}
